/**
 * Micro-signal observable - ~30 lignes pour éviter une dépendance.
 *
 * API minimaliste :
 * - `signal(initial)` -> { get, set, subscribe }
 * - subscribe retourne une fonction de désabonnement
 * - set notifie uniquement si la valeur a changé (référence identique sinon)
 */

export interface Signal<T> {
  get(): T;
  set(v: T): void;
  subscribe(listener: (v: T) => void): () => void;
}

export function signal<T>(initial: T): Signal<T> {
  let value = initial;
  const listeners = new Set<(v: T) => void>();
  return {
    get: () => value,
    set: (v) => {
      if (v === value) return;
      value = v;
      for (const l of listeners) l(value);
    },
    subscribe: (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}

/**
 * Signal dérivé : recalcule une valeur à partir d'un autre signal, réactif.
 * L'abonnement est propagé automatiquement.
 */
export function derived<A, B>(source: Signal<A>, fn: (v: A) => B): Signal<B> {
  const out = signal(fn(source.get()));
  source.subscribe((v) => out.set(fn(v)));
  return out;
}
